import { Link } from "react-router-dom";
import { HERO_IMAGE, HANDSHAKE_IMAGE, LOGO_URL } from "../lib/constants";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  ShieldCheck,
  ArrowRight,
  ClipboardList,
  Users,
  Car,
  AlertTriangle,
  CheckCircle2,
  Lock,
} from "lucide-react";

const Step = ({ num, icon: Icon, title, desc }) => (
  <div className="relative flex flex-col gap-3 p-6 border border-[#E2E8F0] rounded-xl bg-white">
    <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.2em] bg-[#0B192C] text-white px-2 py-1 rounded">
      Step {num}
    </div>
    <Icon className="h-7 w-7 text-[#0B192C]" />
    <div className="font-display text-xl font-semibold text-[#0B192C]">
      {title}
    </div>
    <p className="text-sm text-[#4B5563] leading-relaxed">{desc}</p>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col" data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 ken-burns bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-[#0B192C]/85" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-white">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              You were in an accident.
              <span className="block text-[#FCA5A5]">
                We're here to help.
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-200 max-w-xl leading-relaxed">
              If you were <span className="text-white font-semibold">not at fault</span> in
              a car accident, share the details in a few quick steps. Our intake
              team will reach out the same day.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/start"
                data-testid="hero-start-button"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-semibold px-7 py-4 text-base shadow-lg transition-colors"
              >
                Start My Claim
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
              {[
                ["Same-day", "callback"],
                ["100%", "confidential"],
                ["Free", "intake review"],
              ].map(([a, b]) => (
                <div
                  key={a}
                  className="border border-white/10 rounded-lg px-4 py-3 bg-white/5"
                >
                  <div className="font-display text-2xl font-bold text-white">
                    {a}
                  </div>
                  <div className="text-xs text-slate-300">{b}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 sm:p-8 shadow-2xl ring-1 ring-white/40 max-w-md mx-auto">
              <img
                src={LOGO_URL}
                alt="Car Hurt Help logo"
                className="h-28 w-28 rounded-xl object-cover mx-auto bg-[#0B192C]"
              />
              <div className="text-center mt-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563]">
                  Quick intake — 3 minutes
                </div>
                <h3 className="font-display text-2xl font-bold text-[#0B192C] mt-2">
                  Tell us what happened
                </h3>
                <p className="text-sm text-[#4B5563] mt-2">
                  We'll guide you through a short, mobile-friendly form.
                </p>
              </div>
              <ul className="mt-6 space-y-2.5">
                {[
                  "Your contact info",
                  "Vehicle & insurance details",
                  "Injuries and passengers",
                  "Police report info (optional)",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm text-[#111827]">
                    <CheckCircle2 className="h-4 w-4 text-[#166534]" /> {t}
                  </li>
                ))}
              </ul>
              <Link
                to="/start"
                data-testid="hero-card-start-button"
                className="mt-7 w-full inline-flex justify-center items-center gap-2 rounded-md bg-[#0B192C] hover:bg-[#1A2B4C] text-white font-semibold py-3 transition-colors"
              >
                Begin intake <ArrowRight className="h-4 w-4" />
              </Link>
              <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-[#4B5563]">
                <Lock className="h-3 w-3" /> SSL-encrypted submission
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D32F2F] font-bold">
            How it works
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B192C] mt-3 tracking-tight">
            A guided intake, built for the moment after a crash.
          </h2>
          <p className="text-[#4B5563] mt-4 text-base sm:text-lg">
            Plain language. No legalese. Mobile-first. Fill it out from the
            roadside, at the hospital, or back home.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Step
            num="01"
            icon={ShieldCheck}
            title="Confirm not-at-fault"
            desc="We only assist drivers who were not the at-fault party."
          />
          <Step
            num="02"
            icon={ClipboardList}
            title="Your details"
            desc="Name, contact info, address — quick and confidential."
          />
          <Step
            num="03"
            icon={Car}
            title="Vehicle & insurance"
            desc="Make, model, tag and the carrier on your policy."
          />
          <Step
            num="04"
            icon={Users}
            title="People & police"
            desc="Passengers, injuries, and any police report info."
          />
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-[#F1F5F9] border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#0B192C] font-bold">
              Why Car Hurt Help
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] mt-3 tracking-tight">
              A calm hand after the chaos.
            </h2>
            <p className="text-[#4B5563] mt-4 text-base sm:text-lg leading-relaxed">
              We focus on intake — capturing every detail accurately the first
              time, so you don't have to repeat your story. Your information
              goes straight to a human who follows up the same day.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                ["Local expertise", "Built for accident victims in your area."],
                ["No-fault only", "We protect our network by only accepting not-at-fault claims."],
                ["Privacy first", "We never sell your data. Period."],
              ].map(([t, d]) => (
                <li key={t} className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#166534] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-[#0B192C]">{t}</div>
                    <div className="text-sm text-[#4B5563]">{d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div
              className="aspect-[4/3] rounded-2xl bg-cover bg-center shadow-xl ring-1 ring-[#E2E8F0]"
              style={{ backgroundImage: `url(${HANDSHAKE_IMAGE})` }}
              aria-hidden
            />
            <div className="absolute -bottom-6 -left-6 bg-white p-5 rounded-xl shadow-xl border border-[#E2E8F0] max-w-[260px] hidden sm:block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563]">
                Our promise
              </div>
              <div className="font-display text-lg font-bold text-[#0B192C] mt-1">
                Same-day callback, every time.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AT-FAULT NOTICE */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <AlertTriangle className="h-10 w-10 text-[#D32F2F] mx-auto" />
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C] mt-4">
          A note about at-fault drivers
        </h3>
        <p className="text-[#4B5563] mt-3 max-w-2xl mx-auto">
          Car Hurt Help only accepts intake from drivers who were{" "}
          <span className="font-semibold text-[#0B192C]">not at fault</span>.
          If you were the at-fault party, please contact your own insurance
          carrier directly for guidance.
        </p>
      </section>

      {/* CTA */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <div>
            <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Ready to get help?
            </h3>
            <p className="text-slate-300 mt-2 max-w-xl">
              Start your intake now — it takes about 3 minutes on your phone.
            </p>
          </div>
          <Link
            to="/start"
            data-testid="cta-bottom-start-button"
            className="inline-flex items-center gap-2 rounded-md bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-semibold px-7 py-4 shadow-lg transition-colors"
          >
            Start My Claim <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
